import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import FileUploadCard from '../components/FileUploadCard';
import DirectoryList from '../components/DirectoryList';
import FileList from '../components/FileList';
import { mockDirectories, mockFiles, getFileCategory } from '../mockData';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';

const DashboardPage = () => {
  const { user } = useAuth();
  const [directories, setDirectories] = useState([]);
  const [files, setFiles] = useState([]);
  const [selectedDirectory, setSelectedDirectory] = useState(null);
  const [view, setView] = useState('all'); // 'all' or 'directory'
  const [activeTab, setActiveTab] = useState('files');
  const { toast } = useToast();

  const [trashFiles, setTrashFiles] = useState([]);

  React.useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const { filesApi, directoriesApi } = await import('../lib/api');

      const [fetchedFiles, fetchedDirs, fetchedTrash] = await Promise.all([
        filesApi.list(),
        directoriesApi.list(),
        filesApi.list({ trash: true })
      ]);

      const mapFile = (f) => ({
        id: f.id,
        name: f.filename || 'Unnamed File',
        contentType: f.content_type || 'application/octet-stream',
        size: f.size_bytes || 0,
        isPublic: Boolean(f.is_public),
        directoryId: f.directory_id || getFileCategory(f.content_type || ''),
        url: null,
        autoDeleteIn: f.expires_at ? new Date(f.expires_at * 1000).toLocaleDateString() : 'Never',
        userId: f.user_id,
        allowDelete: Boolean(f.allow_delete),
        accessStatus: f.access_status,
        uploaderName: f.uploader_name,
        uploaderUsername: f.uploader_username || 'anonymous',
        allowDownload: Boolean(f.allow_download),
        isLocked: Boolean(f.is_locked),
        uploaded_at: typeof f.uploaded_at === 'number' ? f.uploaded_at : (f.uploaded_at ? Math.floor(new Date(f.uploaded_at).getTime() / 1000) : undefined),
        uploadedAt: f.uploadedAt || (f.uploaded_at ? new Date((typeof f.uploaded_at === 'number' ? f.uploaded_at * 1000 : Date.parse(f.uploaded_at))).toISOString() : undefined),
        deletedAt: f.deleted_at
      });

      setFiles(fetchedFiles.map(mapFile));
      setTrashFiles(fetchedTrash.map(mapFile));

      // Merge mock auto directories with fetched custom directories
      const autoDirs = mockDirectories.filter(d => d.type === 'auto');
      setDirectories([...autoDirs, ...fetchedDirs]);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load files and directories',
        variant: 'destructive'
      });
    }
  };

  const handleRestoreFile = async (fileId) => {
    try {
      const { filesApi } = await import('../lib/api');
      await filesApi.restore(fileId);

      const restoredFile = trashFiles.find(f => f.id === fileId);
      setTrashFiles(prev => prev.filter(f => f.id !== fileId));
      if (restoredFile) {
        setFiles(prev => [restoredFile, ...prev]);
      }

      toast({
        title: 'File Restored',
        description: 'File has been moved back to your files list.'
      });
    } catch (error) {
      console.error('Failed to restore:', error);
      toast({
        title: 'Restore failed',
        description: 'Failed to restore file.',
        variant: 'destructive'
      });
    }
  };

  const handleFileUpload = (newFile) => {
    setFiles(prev => [newFile, ...prev]);
    // Ensure the new file is visible by resetting the filter
    if (selectedDirectory) {
      setSelectedDirectory(null);
      setView('all');
    }
  };

  const handleCreateDirectory = async (newDirectory) => {
    try {
      const { directoriesApi } = await import('../lib/api');
      const created = await directoriesApi.create(newDirectory.name);
      setDirectories(prev => [...prev, created]);
      toast({
        title: 'Success',
        description: `Directory "${created.name}" created`
      });
    } catch (error) {
      console.error('Failed to create directory:', error);
      toast({
        title: 'Error',
        description: 'Failed to create directory',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteFile = (fileId) => {
    const fileToDelete = files.find(f => f.id === fileId);
    if (fileToDelete) {
      setTrashFiles(prev => [{ ...fileToDelete, deletedAt: new Date().toISOString() }, ...prev]);
    }
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleUpdateFile = (updatedFile) => {
    setFiles(prev => prev.map(f => f.id === updatedFile.id ? { ...f, ...updatedFile } : f));
  };

  const handleFileRequestAccess = async (fileId) => {
    try {
      const { filesApi } = await import('../lib/api');
      const result = await filesApi.requestAccess(fileId);
      setFiles(prev => prev.map(f => f.id === fileId ? { ...f, accessStatus: 'pending' } : f));
      toast({
        title: 'Request sent',
        description: 'Your access request has been sent to the file owner.'
      });
    } catch (error) {
      console.error('Failed to request access:', error);
      toast({
        title: 'Request failed',
        description: 'Failed to send access request.',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteDirectory = (directoryId) => {
    setDirectories(prev => prev.filter(d => d.id !== directoryId));
  };

  const handleDirectoryClick = (directory) => {
    setSelectedDirectory(directory);
    setView('directory');
    setActiveTab('files');
  };

  const handleMoveFile = (fileId, directoryId) => {
    setFiles(prev => prev.map(f => f.id === fileId ? { ...f, directoryId } : f));
  };

  const filteredFiles = selectedDirectory
    ? files.filter(f => {
      if (selectedDirectory.id.startsWith('user-')) {
        const targetUsername = selectedDirectory.id.replace('user-', '');
        return f.uploaderUsername === targetUsername;
      }
      return f.directoryId === selectedDirectory.id;
    })
    : files;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome, {user?.name || user?.username}!</h1>
          <p className="text-gray-600">Manage your files and directories</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border border-gray-200">
            <TabsTrigger value="files">Files & Upload</TabsTrigger>
            <TabsTrigger value="directories">Directories</TabsTrigger>
            <TabsTrigger value="trash">Trash</TabsTrigger>
          </TabsList>

          <TabsContent value="files" className="space-y-6">

            {(user?.is_admin || user?.can_upload !== 0) && (
              <FileUploadCard onFileUpload={handleFileUpload} directories={directories} user={user} />
            )}

            <FileList
              files={filteredFiles}
              user={user}
              directories={directories}
              onDeleteFile={handleDeleteFile}
              onUpdateFile={handleUpdateFile}
              onRequestAccess={handleFileRequestAccess}
              onMoveFile={handleMoveFile}
              selectedDirectory={selectedDirectory}
              onBackToAll={() => {
                setSelectedDirectory(null);
                setView('all');
              }}
            />
          </TabsContent>

          <TabsContent value="directories">
            <DirectoryList
              directories={directories}
              user={user}
              onCreateDirectory={handleCreateDirectory}
              onDeleteDirectory={handleDeleteDirectory}
              onDirectoryClick={handleDirectoryClick}
              files={files}
            />
          </TabsContent>

          <TabsContent value="trash">
            <div className="bg-orange-50 border border-orange-200 text-orange-800 px-4 py-3 rounded-md mb-4 flex items-start">
              <div className="mr-3 mt-0.5">
                <Trash2 className="h-5 w-5" />
              </div>
              <div>
                <h4 className="font-semibold">Trash / Recycle Bin</h4>
                <p className="text-sm mt-1">Files are stored here for 30 days before being permanently deleted.</p>
              </div>
            </div>
            <FileList
              isTrash={true}
              user={user}
              directories={directories}
              files={trashFiles}
              onRestoreFile={handleRestoreFile}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default DashboardPage;
